// src/utils/clientAccounts.js
// Account branding, resolved from the users/{uid} document instead of a
// hardcoded email check. "Client account" is the app's term for any account
// that isn't the owner's; Bodycam Ninja was the first one, and adding another
// should mean writing a user doc, not editing this file.
//
// Fields read off users/{uid}:
//   accountType : 'client' marks the account as a client account. Anything
//                 else (including absent) gets owner branding.
//   displayName : shown in the header and in the admin account switcher.
//   channelUrl  : the YouTube button's target.
//   logoUrl     : absolute URL to a logo. Preferred — a client with one needs
//                 no code change at all.
//   logoKey     : names a logo already committed to src/assets (see
//                 BUNDLED_LOGOS). Only for logos that ship with the bundle.

import maxLogo from '@/assets/logo.png';
import ninjaLogo from '@/assets/ninja.png';

// Branding for the owner's account — also the fallback for any account not
// marked as a client.
export const OWNER_BRANDING = {
  displayName: 'Cop Cam Max',
  dashboardTitle: 'Cop Cam Max Dashboard',
  channelUrl: 'https://www.youtube.com/@copcammax',
  logo: maxLogo,
  // Owner CSV exports keep their long-standing name; client accounts get an
  // unbranded one below.
  exportPrefix: 'copcammax_export',
};

// Logos committed to the repo, addressed by `logoKey`. This map exists only
// for images that were already bundled before branding moved into Firestore.
// New client accounts should use `logoUrl` and stay out of here.
const BUNDLED_LOGOS = {
  ninja: ninjaLogo,
};

export function isClientAccount(profile) {
  return profile?.accountType === 'client';
}

// Resolves the header title, YouTube link and logo for one account.
// `profile` is the users/{uid} document data (or null while it loads).
export function resolveAccountBranding(profile) {
  if (!isClientAccount(profile)) return OWNER_BRANDING;

  const displayName = profile.displayName || profile.email || 'Client';
  return {
    displayName,
    dashboardTitle: `${displayName} Dashboard`,
    // Falls back to the owner's channel so the toolbar button always has a
    // valid target; set channelUrl on the user doc to point it at theirs.
    channelUrl: profile.channelUrl || OWNER_BRANDING.channelUrl,
    logo: profile.logoUrl || BUNDLED_LOGOS[profile.logoKey] || OWNER_BRANDING.logo,
    exportPrefix: 'export',
  };
}

// How one account is labelled in the admin account switcher.
export function accountLabel(profile) {
  return profile?.displayName || profile?.email || 'Unknown account';
}
